import React, { memo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, House, Mail, UserX, X } from 'lucide-react';
import type { CalendarSlot } from '@types';
import { Table, TableBody, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import { Skeleton } from '@core-ui/Components/ui/skeleton.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { getCurrentUser } from '@features/auth';
import moment from 'moment-timezone';

export function DailySkeleton ({ rows = 5 }: { rows?: number }): React.JSX.Element {
  return (
    <>
      <Table>
        <TableHeader className="h-[55px] bg-muted/60">
          <TableRow className="p-2 flex flex-col border-b border-border/60 items-center">
            <Skeleton className="h-6 w-40 rounded-lg bg-muted-foreground/20" />
            <Skeleton className="h-4 w-24 rounded-lg bg-muted-foreground/20 mt-1" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={`s-${i}`} className="border-transparent hover:bg-muted/30" >
              <div className="flex items-center justify-between p-5">
                <Skeleton className="h-4 w-4 rounded-full bg-muted-foreground/20 ml-10" />
                <Skeleton className="h-15 w-[70%] rounded-lg bg-muted-foreground/20 mr-10" />
              </div>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

interface RenderEventsProps {
    groupedSlots: Record<string, CalendarSlot[]>,
    selectedDate: string;
    height: number | null;
    clickBtn: () => void;
}

const RenderEventsComponent = ({ groupedSlots, selectedDate, height, clickBtn }: RenderEventsProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const slotsForDate = groupedSlots[selectedDate] ?? [];
  const userTimezone = getCurrentUser()?.timezone ?? 'UTC';

  function formatDateByLang (date: string, lang: string): string {
    const [month, day] = date.split('/');
    return lang === 'es' ? `${day} - ${month}` : `${month} - ${day}`;
  }

  function formatDayByLang (date: string): string {
    const { t } = useTranslation();
    const dayIndex = new Date(date).getDay();
    const day = [
      ['calendar.day.sunday', 'Sunday'],
      ['calendar.day.monday', 'Monday'],
      ['calendar.day.tuesday', 'Tuesday'],
      ['calendar.day.wednesday', 'Wednesday'],
      ['calendar.day.thursday', 'Thursday'],
      ['calendar.day.friday', 'Friday'],
      ['calendar.day.saturday', 'Saturday']
    ];
    return t(day[dayIndex]);
  }

  return (
    <Table data-qa-id="events-table">
      <TableHeader className="relative bg-muted/60">
        <div className="absolute ml-5 top-1/4">
          <Button data-qa-id="today-button" onClick={clickBtn}>
            {t('calendar.today', 'Today')}
          </Button>
        </div>
        <TableRow className="p-2 flex flex-col border-b border-border/60">
          <div  className="text-center text-lg font-bold" id="day-title">{formatDayByLang(selectedDate)}</div>
          <div  className="mb-1 text-center text-xs font-light" id="day-date">{formatDateByLang(selectedDate, i18n.language)}</div>
        </TableRow>
      </TableHeader>
      <TableBody>
        <div key={selectedDate} className="overflow-y-auto">
          {slotsForDate.length === 0 ? (
            <p data-qa-id="event-not-found" className="text-center text-sm lg:text-base text-foreground mt-4">
              {t('calendar.event.noEvents', 'No appointments scheduled for this day.')}
            </p>
          ) : (
            <div key={selectedDate} className="flex flex-col gap-6 mt-4 items-center" style={{ height: (height != null) ? `${height - 100}px` : 'h-full' }}>
              {slotsForDate.map((slot, index) => {
                const startTime = moment.utc(slot.start, 'M/D/YYYY, h:mm:ss A')
                  .tz(userTimezone)
                  .format('h:mm A');

                const endTime = moment.utc(slot.end, 'M/D/YYYY, h:mm:ss A')
                  .tz(userTimezone)
                  .format('h:mm A');

                return (
                  <div key={`${slot.start}-${index}`} data-qa-id={`event-${index}`} className="hover:bg-muted/50 border border-border rounded-lg pl-10 pr-10 w-[370px]">
                    <span data-qa-id={`time-${index}`} className="text-xl font-bold text-foreground">
                      {startTime}
                      {' - '}
                      {endTime}
                    </span>
                    {slot.status === 'active' ? (
                      <p className="flex flex-row items-center text-sm lg:text-base text-foreground">
                        <Check className="w-4 h-4 text-[var(--phone-check)] mr-4"></Check>
                        {t('calendar.event.status', 'Status: ')}
                        <span className="ml-2">
                          {slot.status.charAt(0).toUpperCase() + slot.status.slice(1)}
                        </span>
                      </p>
                    ) : slot.status === 'canceled' ? (
                      <p className="flex flex-row items-center text-sm lg:text-base text-foreground">
                        <X className="w-4 h-4 text-error mr-4"></X>
                        {t('calendar.event.status', 'Status: ')}
                        <span className="ml-2">
                          {slot.status.charAt(0).toUpperCase() + slot.status.slice(1)}
                        </span>
                      </p> ) : null
                    }
                    {slot.canceledBy !== undefined && (
                      <p className="flex flex-row items-center text-sm lg:text-base text-foreground">
                        <UserX className="w-4 h-4 text-error mr-4"></UserX>
                        {t('calendar.event.canceledBy', 'Canceled by: ')}
                        <span className="ml-2">{slot.canceledBy}</span>
                      </p>
                    )}
                    <p className="flex flex-row items-center text-sm lg:text-base text-foreground">
                      <House className="w-4 h-4 text-sky-500 mr-4"></House>
                      {t('calendar.event.host', 'Host: ')}
                      <span className="break-all ml-2">{slot.host}</span>
                    </p>
                    <p className="flex flex-row items-center text-sm lg:text-base text-foreground">
                      <Mail className="w-4 h-4 text-sky-500 mr-4"></Mail>
                      {slot.guests === 'No guests'
                        ? <>
                          {t('calendar.event.guests', 'Guests: ')}
                          {t('calendar.event.noGuests', 'Sin invitados')}
                        </>
                        : (
                          <>
                            {t('calendar.event.guests', 'Guests: ')}
                            <span className="break-all ml-2">{slot.guests}</span>
                          </>
                        )
                      }
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </TableBody>
    </Table>
  );
};

export const RenderEvents = memo(RenderEventsComponent);